import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../shared/api';
import { DateInput } from '../shared/DateInput';
import { formatDateTime, formatMoney, formatNumber, parseDateInputToIso } from '../shared/formatting';
import { Crumbs, Card as UiCard, Btn, Select as UiSelect, TextInput, Pill, StatCard, ui } from '../shared/ui';
import { LostParcelsTab, DamagedParcelsTab, DriverShipmentsTab, CodControlTab } from './parcelRegisters';
import { InboundShipmentsTab } from './inboundShipmentsTab';

type Locale = 'hr' | 'en';

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';

type Bucket = {
    sent: number;
    delivered: number;
    d24: number;
    d48: number;
    undelivered: number;
    returned: number;
    cancelled: number;
    value: number;
    cost: number;
};

type Row = { period: string; hp: Bucket; gls: Bucket; total: Bucket };

type Granularity = 'day' | 'month' | 'year';

const CONTENT_TAB_KEYS = ['pregled', 'lost', 'damaged', 'driver', 'cod', 'inbound'] as const;
type ContentTab = (typeof CONTENT_TAB_KEYS)[number];
type Tab = ContentTab | 'settings';

type Meta = {
    companies: string[];
    carriers: string[];
    last_imports: Array<{ company_code: string; carrier: string; last_import: string | null; last_file: string | null; n: number }>;
};

type Report = {
    granularity: Granularity;
    filters: Record<string, unknown>;
    summary: { HP: Bucket; GLS: Bucket; TOTAL: Bucket };
    rows: Row[];
};

const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 2px 10px rgba(107,79,28,0.10)', border: '1px solid #efe6d2' };
const inp: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.86rem' };
const th: React.CSSProperties = { textAlign: 'right', padding: '7px 9px', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: BROWN, borderBottom: '2px solid #efe6d2', whiteSpace: 'nowrap' };
const tdR: React.CSSProperties = { padding: '6px 9px', fontSize: '0.84rem', borderBottom: '1px solid #f3ecdb', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
const tdL: React.CSSProperties = { padding: '6px 9px', fontSize: '0.84rem', borderBottom: '1px solid #f3ecdb', textAlign: 'left', whiteSpace: 'nowrap' };

function pct(n: number, d: number): number {
    if (!d) return 0;
    return (n / d) * 100;
}

/** ≥80% zeleno, ≥50% jantar, inače crveno. */
function pctColor(p: number): { bg: string; fg: string } {
    if (p >= 80) return { bg: 'rgba(31,122,54,0.10)', fg: '#1f7a36' };
    if (p >= 50) return { bg: 'rgba(var(--ad-acc-rgb),0.14)', fg: '#9a6f15' };
    return { bg: 'rgba(177,64,64,0.10)', fg: '#b14040' };
}

/** Vraćeno pošiljatelju: HP ISPORUČENO/URUČENO POŠILJATELJU, GLS URUČENO POŠILJATELJU / return. */
function isParcelReturned(p: { carrier?: string; status_norm?: string | null; status_raw?: string | null; returned?: number | boolean | null }): boolean {
    if (p.status_norm === 'returned' || p.returned === 1 || p.returned === true) return true;
    const raw = String(p.status_raw ?? '').toLowerCase();
    const car = String(p.carrier ?? '').toUpperCase();
    if (car === 'HP') {
        return raw.includes('isporučeno pošiljatelju') || raw.includes('uručeno pošiljatelju');
    }
    if (car === 'GLS') {
        return raw.includes('uručeno pošiljatelju') || raw.includes('return');
    }
    return raw.includes('isporučeno pošiljatelju') || raw.includes('uručeno pošiljatelju') || raw.includes('return');
}
function statusBadge(statusNorm: string | null | undefined, t: (hr: string, en: string) => string): { bg: string; fg: string; text: string } {
    switch (statusNorm) {
        case 'delivered': return { bg: '#e4f3e9', fg: '#1f6e45', text: `✅ ${t('Uručeno', 'Delivered')}` };
        case 'in_transit': return { bg: '#fdf3e0', fg: 'var(--ad-acc-3)', text: `🚚 ${t('U dostavi', 'In transit')}` };
        case 'returned': return { bg: '#f7e3e3', fg: '#b14040', text: `↩ ${t('Vraćeno', 'Returned')}` };
        case 'cancelled': return { bg: '#eee', fg: '#777', text: `✖ ${t('Stornirano', 'Cancelled')}` };
        default: return { bg: '#eee', fg: '#777', text: statusNorm ? String(statusNorm) : t('Nema statusa', 'No status') };
    }
}

export function ParcelsModule({
    token,
    locale,
    isSuperadmin = false,
}: {
    token: string;
    locale: Locale;
    isSuperadmin?: boolean;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const isHrms = typeof document !== 'undefined' && document.body.dataset.theme === 'hrms';

    const tabDefs: Array<{ key: ContentTab; label: string }> = [
        { key: 'pregled', label: t('Pregled (HP/GLS)', 'Overview (HP/GLS)') },
        { key: 'lost', label: t('Izgubljene pošiljke', 'Lost parcels') },
        { key: 'damaged', label: t('Oštećeni proizvodi', 'Damaged products') },
        { key: 'driver', label: t('Vozač', 'Driver') },
        { key: 'cod', label: t('Pouzeća (web)', 'COD (web)') },
        { key: 'inbound', label: t('Pristigle', 'Inbound') },
    ];

    const [tab, setTab] = useState<Tab>('pregled');
    const [visibleTabs, setVisibleTabs] = useState<ContentTab[]>([...CONTENT_TAB_KEYS]);
    const [draftVisible, setDraftVisible] = useState<ContentTab[]>([...CONTENT_TAB_KEYS]);
    const [savingSettings, setSavingSettings] = useState(false);
    const [settingsErr, setSettingsErr] = useState('');
    const [meta, setMeta] = useState<Meta | null>(null);
    const [report, setReport] = useState<Report | null>(null);
    const [company, setCompany] = useState('');
    const [granularity, setGranularity] = useState<Granularity>('day');
    const [dateFrom, setDateFrom] = useState(''); // ISO yyyy-mm-dd (DateInput parent state)
    const [dateTo, setDateTo] = useState('');
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');

    // Lookup po broju narudžbe (inline, iznad tablice).
    // Drilldown na konkretne pošiljke za odabrani period
    const [detailPeriod, setDetailPeriod] = useState<string | null>(null);
    const [detailRows, setDetailRows] = useState<any[] | null>(null);
    const [detailBusy, setDetailBusy] = useState(false);
    const [detailStatus, setDetailStatus] = useState<string>('all'); // all | undelivered | returned | delivered | in_transit | cancelled
    const [pregledMode, setPregledMode] = useState<'overview' | 'returned'>('overview');
    const [returnedRows, setReturnedRows] = useState<any[] | null>(null);
    const [returnedBusy, setReturnedBusy] = useState(false);
    const openDetail = useCallback(async (period: string) => {
        setDetailPeriod(period); setDetailRows(null); setDetailBusy(true); setDetailStatus('all');
        try {
            const params = new URLSearchParams({ period, granularity });
            if (company) params.set('company_code', company);
            const r = await api<{ rows: any[] }>(`/parcels/detail?${params}`, token);
            setDetailRows(r.rows);
        } catch { setDetailRows([]); } finally { setDetailBusy(false); }
    }, [token, granularity, company]);

    const [lookupOrder, setLookupOrder] = useState('');
    const [lookupResult, setLookupResult] = useState<any | undefined>(undefined); // undefined = nije traženo; null = nije pronađeno
    const [lookupBusy, setLookupBusy] = useState(false);
    const doLookup = useCallback(async () => {
        const ord = lookupOrder.trim();
        if (!ord) { setLookupResult(undefined); return; }
        setLookupBusy(true);
        try {
            const r = await api<{ links: Record<string, any> }>(`/parcels/by-orders?orders=${encodeURIComponent(ord)}`, token);
            setLookupResult(r.links?.[ord] ?? null);
        } catch {
            setLookupResult(null);
        } finally {
            setLookupBusy(false);
        }
    }, [lookupOrder, token]);

    // Carrier je za sad uvijek HP (jedini izvor); ostavljamo prazno = svi.
    const carrier = '';

    useEffect(() => {
        (async () => {
            try {
                const m = await api<Meta>('/parcels/meta', token);
                setMeta(m);
            } catch (e) {
                setErr(e instanceof Error ? e.message : 'API');
            }
        })();
    }, [token]);

    useEffect(() => {
        (async () => {
            try {
                const s = await api<{ tabs: ContentTab[]; visible: ContentTab[]; can_edit: boolean }>('/parcels/settings', token);
                const vis = (s.visible ?? []).filter((k): k is ContentTab => (CONTENT_TAB_KEYS as readonly string[]).includes(k));
                const next = vis.length > 0 ? vis : [...CONTENT_TAB_KEYS];
                setVisibleTabs(next);
                setDraftVisible(next);
            } catch {
                // default: svi tabovi
            }
        })();
    }, [token]);

    const queryString = useMemo(() => {
        const qp = new URLSearchParams();
        if (company) qp.set('company_code', company);
        if (carrier) qp.set('carrier', carrier);
        // dateFrom/dateTo su već ISO (DateInput drži ISO); šalji samo ako su validni.
        const fromIso = dateFrom ? parseDateInputToIso(dateFrom) ?? (/^\d{4}-\d{2}-\d{2}$/.test(dateFrom) ? dateFrom : null) : null;
        const toIso = dateTo ? parseDateInputToIso(dateTo) ?? (/^\d{4}-\d{2}-\d{2}$/.test(dateTo) ? dateTo : null) : null;
        if (fromIso) qp.set('date_from', fromIso);
        if (toIso) qp.set('date_to', toIso);
        qp.set('granularity', granularity);
        return qp.toString();
    }, [company, carrier, dateFrom, dateTo, granularity]);

    const load = useCallback(async () => {
        setLoading(true);
        setErr('');
        try {
            const r = await api<Report>(`/parcels/report?${queryString}`, token);
            setReport(r);
        } catch (e) {
            setErr(e instanceof Error ? e.message : 'API');
            setReport(null);
        } finally {
            setLoading(false);
        }
    }, [queryString, token]);

    useEffect(() => { void load(); }, [load]);

    const loadReturned = useCallback(async () => {
        setReturnedBusy(true);
        try {
            const r = await api<{ rows: any[]; count: number }>(`/parcels/returned?${queryString}`, token);
            setReturnedRows(r.rows ?? []);
        } catch {
            setReturnedRows([]);
        } finally {
            setReturnedBusy(false);
        }
    }, [queryString, token]);

    useEffect(() => {
        if (pregledMode === 'returned') {
            void loadReturned();
        }
    }, [pregledMode, loadReturned]);

    useEffect(() => {
        if (tab === 'settings') return;
        if (!visibleTabs.includes(tab)) {
            setTab(visibleTabs[0] ?? 'pregled');
        }
    }, [visibleTabs, tab]);

    const saveTabVisibility = async () => {
        if (draftVisible.length === 0) {
            setSettingsErr(t('Ostavi barem jedan tab vidljiv.', 'Keep at least one tab visible.'));
            return;
        }
        setSavingSettings(true);
        setSettingsErr('');
        try {
            const res = await mutate<{ ok: boolean; visible: ContentTab[] }>('/parcels/settings', token, 'POST', { visible: draftVisible });
            const next = (res.visible ?? draftVisible).filter((k): k is ContentTab => (CONTENT_TAB_KEYS as readonly string[]).includes(k));
            setVisibleTabs(next.length > 0 ? next : [...CONTENT_TAB_KEYS]);
            setDraftVisible(next.length > 0 ? next : [...CONTENT_TAB_KEYS]);
        } catch (e) {
            setSettingsErr(e instanceof Error ? e.message : t('Spremanje nije uspjelo.', 'Save failed.'));
        } finally {
            setSavingSettings(false);
        }
    };

    async function exportXls() {
        const { downloadFile } = await import('../shared/api');
        try {
            await downloadFile(`/parcels/export?${queryString}`, token, 'posiljke.xlsx');
        } catch (e) {
            alert('Export FAIL: ' + (e instanceof Error ? e.message : 'greška'));
        }
    }

    const summary = report?.summary;
    const tot = summary?.TOTAL;
    const hpVal = summary?.HP.value ?? 0;
    const glsVal = summary?.GLS.value ?? 0;

    const p24 = tot ? pct(tot.d24, tot.sent) : 0;
    const p48 = tot ? pct(tot.d48, tot.sent) : 0;
    const c24 = pctColor(p24);
    const c48 = pctColor(p48);

    const fmtN = (n: number) => formatNumber(n, locale);
    const fmtE = (n: number) => formatMoney(n, locale);
    const cpp = (b: Bucket) => (b.sent > 0 ? b.cost / b.sent : 0); // trošak po paketu

    const [impBusy, setImpBusy] = useState(false);
    const [impResult, setImpResult] = useState<string | null>(null);
    async function doImport(file: File) {
        setImpBusy(true); setImpResult(null);
        const fd = new FormData();
        fd.append('file', file);
        if (company) fd.append('company_code', company);
        try {
            const { uploadFile } = await import('../shared/api');
            const r = await uploadFile<{ ok: boolean; total: number; upserted: number; company_code: string | null; error?: string }>('/parcels/import', token, fd);
            if (!r.ok) { setImpResult('⚠ ' + (r.error ?? t('Uvoz nije uspio.', 'Import failed.'))); }
            else { setImpResult(t(`✓ Uvezeno ${r.upserted}/${r.total} (${r.company_code ?? '?'})`, `✓ Imported ${r.upserted}/${r.total} (${r.company_code ?? '?'})`)); }
        } catch (e) { setImpResult('⚠ ' + String((e as Error)?.message ?? e)); }
        finally { setImpBusy(false); }
    }

    const tableInner = (
        <>
            {err ? <p className="error" style={{ padding: 16, margin: 0 }}>{err}</p> : null}
            {loading ? <p className="muted" style={{ padding: 16, margin: 0 }}>{t('Učitavam…', 'Loading…')}</p> : null}
            {!loading && !err && report && report.rows.length === 0 ? (
                <p className="muted" style={{ padding: 16, margin: 0, fontStyle: 'italic' }}>{t('Nema podataka za odabrane filtere.', 'No data for the selected filters.')}</p>
            ) : null}
            {!loading && !err && report && report.rows.length > 0 ? (
                <div className="table-wrap" style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1100 }}>
                        <thead>
                            <tr>
                                <th style={{ ...th, textAlign: 'left', borderRight: '1px solid #efe6d2' }}>{t('Razdoblje', 'Period')}</th>
                                <th style={{ ...th, background: 'rgba(var(--ad-acc-rgb),0.06)' }} colSpan={4}>HP</th>
                                <th style={{ ...th, background: 'rgba(120,120,120,0.05)', color: '#999' }} colSpan={4}>GLS</th>
                                <th style={{ ...th, background: 'rgba(107,79,28,0.06)' }} colSpan={4}>{t('UKUPNO', 'TOTAL')}</th>
                                <th style={{ ...th, borderLeft: '1px solid #efe6d2' }} colSpan={3}>{t('Vrijednost poslanih', 'Sent value')}</th>
                                <th style={{ ...th, borderLeft: '1px solid #efe6d2', background: 'rgba(107,79,28,0.06)' }} colSpan={4}>{t('Trošak kurira (ulazni računi)', 'Carrier cost (invoices)')}</th>
                            </tr>
                            <tr>
                                <th style={{ ...th, textAlign: 'left', borderRight: '1px solid #efe6d2' }} />
                                <SubTh>{t('paketi', 'parcels')}</SubTh><SubTh>≤24h</SubTh><SubTh>≤48h</SubTh><SubTh>{t('neuruč.', 'undeliv.')}</SubTh>
                                <SubTh muted>{t('paketi', 'parcels')}</SubTh><SubTh muted>≤24h</SubTh><SubTh muted>≤48h</SubTh><SubTh muted>{t('neuruč.', 'undeliv.')}</SubTh>
                                <SubTh>{t('paketi', 'parcels')}</SubTh><SubTh>≤24h</SubTh><SubTh>≤48h</SubTh><SubTh>{t('neuruč.', 'undeliv.')}</SubTh>
                                <SubTh>{t('HP €', 'HP €')}</SubTh><SubTh muted>{t('GLS €', 'GLS €')}</SubTh><SubTh>{t('ukupno €', 'total €')}</SubTh>
                                <SubTh>HP €</SubTh><SubTh muted>GLS €</SubTh><SubTh>{t('ukupno €', 'total €')}</SubTh><SubTh>€/pkt</SubTh>
                            </tr>
                        </thead>
                        <tbody>
                            {/* UKUPNO summary row (vrh) */}
                            {summary ? (
                                <tr style={{ background: '#faf6ec', fontWeight: 700 }}>
                                    <td style={{ ...tdL, fontWeight: 700, borderRight: '1px solid #efe6d2' }}>{t('UKUPNO', 'TOTAL')}</td>
                                    <NumCell v={summary.HP.sent} f={fmtN} bold />
                                    <NumCell v={summary.HP.d24} f={fmtN} bold />
                                    <NumCell v={summary.HP.d48} f={fmtN} bold />
                                    <NumCell v={summary.HP.undelivered} f={fmtN} bold />
                                    <NumCell v={summary.GLS.sent} f={fmtN} bold muted />
                                    <NumCell v={summary.GLS.d24} f={fmtN} bold muted />
                                    <NumCell v={summary.GLS.d48} f={fmtN} bold muted />
                                    <NumCell v={summary.GLS.undelivered} f={fmtN} bold muted />
                                    <NumCell v={summary.TOTAL.sent} f={fmtN} bold />
                                    <NumCell v={summary.TOTAL.d24} f={fmtN} bold />
                                    <NumCell v={summary.TOTAL.d48} f={fmtN} bold />
                                    <NumCell v={summary.TOTAL.undelivered} f={fmtN} bold />
                                    <td style={{ ...tdR, fontWeight: 700, borderLeft: '1px solid #efe6d2' }}>{fmtE(summary.HP.value)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#999' }}>{fmtE(summary.GLS.value)}</td>
                                    <td style={{ ...tdR, fontWeight: 700 }}>{fmtE(summary.TOTAL.value)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, borderLeft: '1px solid #efe6d2' }}>{fmtE(summary.HP.cost)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#999' }}>{fmtE(summary.GLS.cost)}</td>
                                    <td style={{ ...tdR, fontWeight: 700 }}>{fmtE(summary.TOTAL.cost)}</td>
                                    <td style={{ ...tdR, fontWeight: 700 }}>{fmtE(cpp(summary.TOTAL))}</td>
                                </tr>
                            ) : null}
                            {report.rows.map((r) => (
                                <tr key={r.period}>
                                    <td style={{ ...tdL, borderRight: '1px solid #efe6d2' }}>
                                        <button type="button" onClick={() => void openDetail(r.period)} title={t('Prikaži konkretne pošiljke', 'Show individual parcels')}
                                            style={{ background: 'transparent', border: 0, color: 'var(--ad-acc-3)', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline', padding: 0, font: 'inherit' }}>
                                            {r.period}
                                        </button>
                                    </td>
                                    <NumCell v={r.hp.sent} f={fmtN} />
                                    <NumCell v={r.hp.d24} f={fmtN} />
                                    <NumCell v={r.hp.d48} f={fmtN} />
                                    <NumCell v={r.hp.undelivered} f={fmtN} />
                                    <NumCell v={r.gls.sent} f={fmtN} muted />
                                    <NumCell v={r.gls.d24} f={fmtN} muted />
                                    <NumCell v={r.gls.d48} f={fmtN} muted />
                                    <NumCell v={r.gls.undelivered} f={fmtN} muted />
                                    <NumCell v={r.total.sent} f={fmtN} />
                                    <NumCell v={r.total.d24} f={fmtN} />
                                    <NumCell v={r.total.d48} f={fmtN} />
                                    <NumCell v={r.total.undelivered} f={fmtN} />
                                    <td style={{ ...tdR, borderLeft: '1px solid #efe6d2' }}>{fmtE(r.hp.value)}</td>
                                    <td style={{ ...tdR, color: '#999' }}>{fmtE(r.gls.value)}</td>
                                    <td style={{ ...tdR }}>{fmtE(r.total.value)}</td>
                                    <td style={{ ...tdR, borderLeft: '1px solid #efe6d2' }}>{fmtE(r.hp.cost)}</td>
                                    <td style={{ ...tdR, color: '#999' }}>{fmtE(r.gls.cost)}</td>
                                    <td style={{ ...tdR }}>{fmtE(r.total.cost)}</td>
                                    <td style={{ ...tdR }}>{fmtE(cpp(r.total))}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
            {detailPeriod ? (
                <div onClick={() => setDetailPeriod(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(30,20,5,0.45)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 12px', overflowY: 'auto' }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: 940, maxWidth: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 12, flexWrap: 'wrap' }}>
                            <h3 style={{ margin: 0, color: BROWN, fontSize: '1.05rem' }}>{t('Pošiljke', 'Parcels')} — {detailPeriod}</h3>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
                                <label style={{ fontSize: '0.82rem', display: 'flex', gap: 6, alignItems: 'center' }}>
                                    <span className="muted">{t('Status', 'Status')}:</span>
                                    <select value={detailStatus} onChange={(e) => setDetailStatus(e.target.value)} style={{ padding: '5px 8px', fontSize: '0.82rem', borderRadius: 6 }}>
                                        <option value="all">{t('Sve', 'All')}</option>
                                        <option value="undelivered">{t('Neuručeno', 'Undelivered')}</option>
                                        <option value="returned">↩ {t('Vraćene', 'Returned')}</option>
                                        <option value="delivered">✅ {t('Uručene', 'Delivered')}</option>
                                        <option value="in_transit">🚚 {t('U dostavi', 'In transit')}</option>
                                        <option value="cancelled">✖ {t('Stornirane', 'Cancelled')}</option>
                                    </select>
                                </label>
                                <button onClick={() => setDetailPeriod(null)} style={{ border: 'none', background: 'transparent', fontSize: '1.4rem', cursor: 'pointer', color: '#8a7a52' }}>×</button>
                            </div>
                        </div>
                        {detailBusy ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
                        {!detailBusy && detailRows ? (() => {
                            const shownRows = detailRows.filter((p) => {
                                const ret = isParcelReturned(p);
                                const cancelled = Boolean(p.cancelled);
                                const delivered = p.status_norm === 'delivered';
                                if (detailStatus === 'all') return true;
                                if (detailStatus === 'returned') return ret && !cancelled;
                                if (detailStatus === 'undelivered') return !delivered && !cancelled && !ret;
                                if (detailStatus === 'in_transit') return p.status_norm === 'in_transit' && !ret && !cancelled;
                                if (detailStatus === 'delivered') return delivered;
                                if (detailStatus === 'cancelled') return cancelled;
                                return p.status_norm === detailStatus;
                            });
                            const returnedCnt = detailRows.filter((p) => isParcelReturned(p) && !p.cancelled).length;
                            return (
                            <div style={{ overflowX: 'auto', maxHeight: '66vh' }}>
                                {returnedCnt > 0 && detailStatus === 'all' ? <div style={{ marginBottom: 8 }}><button type="button" onClick={() => setDetailStatus('returned')} style={{ background: '#f7e3e3', color: '#b14040', border: '1px solid #e3bcbc', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>↩ {t('Izdvoji vraćene', 'Show returned')} ({returnedCnt})</button></div> : null}
                                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 820 }}>
                                    <thead><tr>{['Kurir', 'Barkod', 'Status', 'Poslano', 'Isporučeno', 'h', 'Vrijednost', 'Mjesto'].map((h) => <th key={h} style={{ ...th, textAlign: 'left' }}>{h}</th>)}</tr></thead>
                                    <tbody>
                                        {shownRows.map((p, i) => (
                                            <tr key={i} style={isParcelReturned(p) ? { background: '#fbf1f1' } : undefined}>
                                                <td style={tdL}>{p.carrier}</td>
                                                <td style={tdL}>{p.barcode}</td>
                                                <td style={tdL}>{isParcelReturned(p) ? '↩ ' : (p.cancelled ? '⊘ ' : '')}{p.status_raw ?? p.status_norm}</td>
                                                <td style={tdL}>{p.sent_at ? formatDateTime(p.sent_at, locale) : '—'}</td>
                                                <td style={tdL}>{p.delivered_at ? formatDateTime(p.delivered_at, locale) : '—'}</td>
                                                <td style={tdR}>{p.delivery_hours != null ? Math.round(Number(p.delivery_hours)) : '—'}</td>
                                                <td style={tdR}>{fmtE(Number(p.value_eur || 0))}</td>
                                                <td style={tdL}>{p.recipient_place ?? ''}</td>
                                            </tr>
                                        ))}
                                        {shownRows.length === 0 ? <tr><td style={tdL} colSpan={8}>{t('Nema pošiljki.', 'No parcels.')}</td></tr> : null}
                                    </tbody>
                                </table>
                                <div style={{ fontSize: '0.74rem', color: '#8a7a52', marginTop: 6 }}>{shownRows.length} {t('pošiljki', 'parcels')}{detailStatus !== 'all' ? ` · ${t('filter', 'filter')}: ${detailStatus}` : ''}</div>
                            </div>
                            );
                        })() : null}
                    </div>
                </div>
            ) : null}
        </>
    );

    const returnedInner = (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                <h3 style={{ margin: 0, color: BROWN, fontSize: '1.05rem' }}>{t('Vraćene (neisporučene)', 'Returned (undelivered)')}</h3>
                <span className="muted" style={{ fontSize: '0.78rem' }}>
                    HP: ISPORUČENO POŠILJATELJU · GLS: URUČENO POŠILJATELJU / Returned to sender
                </span>
            </div>
            {returnedBusy ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
            {!returnedBusy && returnedRows ? (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 820 }}>
                        <thead>
                            <tr>
                                {['Kurir', 'Barkod', 'Status', 'Firma', 'Poslano', 'Status u', 'Vrijednost', 'Mjesto', 'Narudžba'].map((h) => (
                                    <th key={h} style={{ ...th, textAlign: 'left' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {returnedRows.map((p, i) => (
                                <tr key={`${p.carrier}-${p.barcode}-${i}`} style={{ background: '#fbf1f1' }}>
                                    <td style={tdL}>{p.carrier}</td>
                                    <td style={tdL}>{p.barcode}</td>
                                    <td style={tdL}>↩ {p.status_raw ?? p.status_norm}</td>
                                    <td style={tdL}>{p.company_code ?? ''}</td>
                                    <td style={tdL}>{p.sent_at ? formatDateTime(p.sent_at, locale) : '—'}</td>
                                    <td style={tdL}>{p.status_at ? formatDateTime(p.status_at, locale) : '—'}</td>
                                    <td style={tdR}>{fmtE(Number(p.value_eur || 0))}</td>
                                    <td style={tdL}>{p.recipient_place ?? ''}</td>
                                    <td style={tdL}>{p.order_number ?? ''}</td>
                                </tr>
                            ))}
                            {returnedRows.length === 0 ? (
                                <tr><td style={tdL} colSpan={9}>{t('Nema vraćenih pošiljki za odabrane filtere.', 'No returned parcels for the selected filters.')}</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                    <div style={{ fontSize: '0.74rem', color: '#8a7a52', marginTop: 6 }}>{returnedRows.length} {t('vraćenih', 'returned')}</div>
                </div>
            ) : null}
        </>
    );

    return (
        <div style={{ ...(isHrms ? {} : { maxWidth: 1240, margin: '0 auto' }), padding: '8px 4px 40px' }}>
            {isHrms ? (
                <div style={{ marginBottom: 16 }}>
                    <Crumbs items={[t('Dashboard', 'Dashboard'), t('Pošiljke', 'Parcels')]} />
                </div>
            ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: `linear-gradient(135deg, ${GOLD}, ${BROWN})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>📦</div>
                <div>
                    <h1 style={{ margin: 0, color: BROWN, fontSize: '1.4rem' }}>{t('Pošiljke', 'Parcels')}</h1>
                    <div className="muted" style={{ fontSize: '0.82rem' }}>{t('Kontrolni pregled poslanih paketa (HP, GLS)', 'Control overview of sent parcels (HP, GLS)')}</div>
                </div>
            </div>
            )}

            {/* Tabovi */}
            <div style={{ display: 'flex', gap: 6, marginTop: 14, marginBottom: 4, flexWrap: 'wrap' }}>
                {tabDefs.filter((d) => visibleTabs.includes(d.key)).map(({ key, label }) => (
                    <button key={key} type="button" onClick={() => setTab(key)} style={{
                        padding: '7px 14px', borderRadius: 9, border: '1px solid ' + (tab === key ? BROWN : '#e0d4b6'),
                        background: tab === key ? BROWN : '#fff', color: tab === key ? '#fff' : BROWN, fontWeight: 700,
                        fontSize: '0.82rem', cursor: 'pointer',
                    }}>{label}</button>
                ))}
                {isSuperadmin ? (
                    <button type="button" onClick={() => setTab('settings')} style={{
                        padding: '7px 14px', borderRadius: 9, border: '1px solid ' + (tab === 'settings' ? BROWN : '#e0d4b6'),
                        background: tab === 'settings' ? BROWN : '#fff', color: tab === 'settings' ? '#fff' : BROWN, fontWeight: 700,
                        fontSize: '0.82rem', cursor: 'pointer', marginLeft: 'auto',
                    }}>⚙ {t('Postavke', 'Settings')}</button>
                ) : null}
            </div>

            {tab === 'settings' && isSuperadmin ? (
                <div style={{ ...card, marginTop: 12 }}>
                    <strong style={{ color: BROWN }}>{t('Vidljivost tabova', 'Tab visibility')}</strong>
                    <p className="muted" style={{ margin: '6px 0 12px', fontSize: '0.82rem' }}>
                        {t('Samo superadministrator mijenja što ostali vide u ovom modulu.', 'Only a superadministrator changes what others see in this module.')}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                        {tabDefs.map((d) => {
                            const on = draftVisible.includes(d.key);
                            return (
                                <label key={d.key} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={on}
                                        onChange={(e) => {
                                            setDraftVisible((prev) => {
                                                if (e.target.checked) return CONTENT_TAB_KEYS.filter((k) => k === d.key || prev.includes(k));
                                                return prev.filter((k) => k !== d.key);
                                            });
                                        }}
                                    />
                                    <span>{d.label}</span>
                                </label>
                            );
                        })}
                    </div>
                    {settingsErr ? <p className="error" style={{ marginTop: 10 }}>{settingsErr}</p> : null}
                    <div style={{ marginTop: 14 }}>
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={savingSettings || draftVisible.length === 0}
                            onClick={() => void saveTabVisibility()}
                        >
                            {savingSettings ? t('Spremanje...', 'Saving...') : t('Spremi vidljivost', 'Save visibility')}
                        </button>
                    </div>
                </div>
            ) : null}

            {tab === 'lost' ? <div style={{ marginTop: 12 }}><LostParcelsTab token={token} locale={locale} /></div> : null}
            {tab === 'damaged' ? <div style={{ marginTop: 12 }}><DamagedParcelsTab token={token} locale={locale} /></div> : null}
            {tab === 'driver' ? <div style={{ marginTop: 12 }}><DriverShipmentsTab token={token} locale={locale} /></div> : null}
            {tab === 'cod' ? <div style={{ marginTop: 12 }}><CodControlTab token={token} locale={locale} /></div> : null}
            {tab === 'inbound' ? <div style={{ marginTop: 12 }}><InboundShipmentsTab token={token} locale={locale} /></div> : null}

            {tab === 'pregled' ? (<>
            {/* Uvoz xlsx (ručno) — dedup po barkodu */}
            {isHrms ? (
                <UiCard pad={16} style={{ marginTop: 12 }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        <strong style={{ color: ui.ink }}>⬆ {t('Uvoz xlsx (GLS / HP)', 'Import xlsx (GLS / HP)')}</strong>
                        <input type="file" accept=".xlsx,.xls" disabled={impBusy} onChange={(e) => { const fl = e.target.files?.[0]; if (fl) void doImport(fl); e.target.value = ''; }} />
                        {impBusy ? <span style={{ color: ui.muted }}>{t('Uvozim…', 'Importing…')}</span> : null}
                        {impResult ? <span style={{ fontSize: '0.84rem', color: impResult.startsWith('✓') ? '#1f7a36' : '#dc2626' }}>{impResult}</span> : null}
                        <span style={{ fontSize: '0.74rem', color: ui.muted, marginLeft: 'auto' }}>{t('Dedup po barkodu — postojeće se ažurira. Auto i preko obrada@.', 'Dedup by barcode — existing updated. Also via obrada@.')}</span>
                    </div>
                </UiCard>
            ) : (
            <div style={{ ...card, marginTop: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ color: BROWN }}>⬆ {t('Uvoz xlsx (GLS / HP)', 'Import xlsx (GLS / HP)')}</strong>
                <input type="file" accept=".xlsx,.xls" disabled={impBusy} onChange={(e) => { const fl = e.target.files?.[0]; if (fl) void doImport(fl); e.target.value = ''; }} />
                {impBusy ? <span className="muted">{t('Uvozim…', 'Importing…')}</span> : null}
                {impResult ? <span style={{ fontSize: '0.84rem', color: impResult.startsWith('✓') ? '#1f6e45' : '#b14040' }}>{impResult}</span> : null}
                <span className="muted" style={{ fontSize: '0.74rem', marginLeft: 'auto' }}>{t('Dedup po barkodu — postojeće se ažurira. Auto i preko obrada@.', 'Dedup by barcode — existing updated. Also via obrada@.')}</span>
            </div>
            )}

            {/* Filteri */}
            {isHrms ? (
                <UiCard pad={16} style={{ marginTop: 14 }}>
                    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.74rem', color: ui.ink2, fontWeight: 600 }}>
                            {t('Firma', 'Company')}
                            <UiSelect value={company} onChange={setCompany} width={170}>
                                <option value="">── {t('Sve firme', 'All companies')} ──</option>
                                {(meta?.companies ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
                            </UiSelect>
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.74rem', color: ui.ink2, fontWeight: 600 }}>
                            {t('Granularnost', 'Granularity')}
                            <UiSelect value={granularity} onChange={(v) => setGranularity(v as Granularity)} width={150}>
                                <option value="day">{t('Po danu', 'By day')}</option>
                                <option value="month">{t('Po mjesecu', 'By month')}</option>
                                <option value="year">{t('Po godini', 'By year')}</option>
                            </UiSelect>
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.74rem', color: ui.ink2, fontWeight: 600 }}>
                            {t('Datum od', 'Date from')}
                            <div style={{ width: 170 }}><DateInput value={dateFrom} onChange={setDateFrom} /></div>
                        </label>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.74rem', color: ui.ink2, fontWeight: 600 }}>
                            {t('Datum do', 'Date to')}
                            <div style={{ width: 170 }}><DateInput value={dateTo} onChange={setDateTo} /></div>
                        </label>
                        <Btn variant="soft" onClick={() => void exportXls()}>📥 {t('Izvoz (xls)', 'Export (xls)')}</Btn>
                        <Btn
                            variant={pregledMode === 'returned' ? 'primary' : 'soft'}
                            onClick={() => setPregledMode((m) => (m === 'returned' ? 'overview' : 'returned'))}
                        >
                            ↩ {pregledMode === 'returned' ? t('Natrag na pregled', 'Back to overview') : t('Vraćene (neisporučene)', 'Returned (undelivered)')}
                            {tot && (tot.returned ?? 0) > 0 ? ` (${fmtN(tot.returned)})` : ''}
                        </Btn>
                    </div>
                </UiCard>
            ) : (
            <div style={{ ...card, marginTop: 14, display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.74rem', color: BROWN, fontWeight: 600 }}>
                    {t('Firma', 'Company')}
                    <select value={company} onChange={(e) => setCompany(e.target.value)} style={{ ...inp, minWidth: 160 }}>
                        <option value="">── {t('Sve firme', 'All companies')} ──</option>
                        {(meta?.companies ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.74rem', color: BROWN, fontWeight: 600 }}>
                    {t('Granularnost', 'Granularity')}
                    <select value={granularity} onChange={(e) => setGranularity(e.target.value as Granularity)} style={{ ...inp, minWidth: 140 }}>
                        <option value="day">{t('Po danu', 'By day')}</option>
                        <option value="month">{t('Po mjesecu', 'By month')}</option>
                        <option value="year">{t('Po godini', 'By year')}</option>
                    </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.74rem', color: BROWN, fontWeight: 600 }}>
                    {t('Datum od', 'Date from')}
                    <div style={{ width: 170 }}><DateInput value={dateFrom} onChange={setDateFrom} /></div>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.74rem', color: BROWN, fontWeight: 600 }}>
                    {t('Datum do', 'Date to')}
                    <div style={{ width: 170 }}><DateInput value={dateTo} onChange={setDateTo} /></div>
                </label>
                <button type="button" onClick={() => void exportXls()}
                    style={{ padding: '7px 12px', cursor: 'pointer', border: '1px solid rgba(var(--ad-soft-rgb),0.5)', borderRadius: 8, background: '#fffdf6', fontSize: '0.84rem', color: BROWN, fontWeight: 600 }}>
                    📥 {t('Izvoz (xls)', 'Export (xls)')}
                </button>
                <button type="button" onClick={() => setPregledMode((m) => (m === 'returned' ? 'overview' : 'returned'))}
                    style={{
                        padding: '7px 12px', cursor: 'pointer',
                        border: '1px solid ' + (pregledMode === 'returned' ? '#c48a8a' : 'rgba(var(--ad-soft-rgb),0.5)'),
                        borderRadius: 8,
                        background: pregledMode === 'returned' ? '#f7e3e3' : '#fffdf6',
                        fontSize: '0.84rem', color: pregledMode === 'returned' ? '#b14040' : BROWN, fontWeight: 600,
                    }}>
                    ↩ {pregledMode === 'returned' ? t('Natrag na pregled', 'Back to overview') : t('Vraćene (neisporučene)', 'Returned (undelivered)')}
                    {tot && (tot.returned ?? 0) > 0 ? ` (${fmtN(tot.returned)})` : ''}
                </button>
            </div>
            )}

            {/* Uvoz banner */}
            {meta && meta.last_imports.length > 0 ? (
                <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {meta.last_imports.map((li) => (
                        <span key={`${li.company_code}|${li.carrier}`} className="muted"
                            style={{ fontSize: '0.74rem', background: '#faf6ec', border: '1px solid #efe6d2', borderRadius: 8, padding: '5px 10px' }}>
                            📥 {t('Zadnji uvoz', 'Last import')}: <strong>{li.company_code}/{li.carrier}</strong>
                            {' — '}{li.last_import ? formatDateTime(li.last_import, locale) : '—'}
                            {' '}({fmtN(li.n)} {t('paketa', 'parcels')}{li.last_file ? `, ${li.last_file}` : ''})
                        </span>
                    ))}
                </div>
            ) : null}

            {/* KPI pločice */}
            {tot ? (
                isHrms ? (
                    <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                        <StatCard icon="📦" color="#6366f1" value={fmtN(tot.sent)} label={t('Poslano', 'Sent')} />
                        <StatCard icon="✅" color="#10b981" value={fmtN(tot.delivered)} label={t('Uručeno', 'Delivered')} />
                        <StatCard icon="⚡" color={c24.fg} value={fmtN(tot.d24)} label="≤24h" sub={`${p24.toFixed(0)}%`} subColor={c24.fg} />
                        <StatCard icon="🕑" color={c48.fg} value={fmtN(tot.d48)} label="≤48h" sub={`${p48.toFixed(0)}%`} subColor={c48.fg} />
                        <StatCard icon="↩" color="#f43f5e" value={fmtN(tot.undelivered)} label={t('Neuručeno', 'Undelivered')} />
                        <div onClick={() => setPregledMode('returned')} style={{ cursor: 'pointer' }}>
                            <StatCard icon="↩" color="#b14040" value={fmtN(tot.returned)} label={t('Vraćene', 'Returned')} />
                        </div>
                        <StatCard icon="💶" color="#0ea5e9" value={fmtE(hpVal)} label={t('Vrijednost HP', 'HP value')} />
                        <StatCard icon="💶" color="#64748b" value={fmtE(glsVal)} label={t('Vrijednost GLS', 'GLS value')} />
                    </div>
                ) : (
                <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                    <KpiTile label={t('Poslano', 'Sent')} value={fmtN(tot.sent)} />
                    <KpiTile label={t('Uručeno', 'Delivered')} value={fmtN(tot.delivered)} />
                    <KpiTile label="≤24h" value={fmtN(tot.d24)} sub={`${p24.toFixed(0)}%`} bg={c24.bg} fg={c24.fg} />
                    <KpiTile label="≤48h" value={fmtN(tot.d48)} sub={`${p48.toFixed(0)}%`} bg={c48.bg} fg={c48.fg} />
                    <KpiTile label={t('Neuručeno', 'Undelivered')} value={fmtN(tot.undelivered)} />
                    <div onClick={() => setPregledMode('returned')} style={{ cursor: 'pointer' }}>
                        <KpiTile label={t('Vraćene', 'Returned')} value={fmtN(tot.returned)} bg="#fbf1f1" fg="#b14040" />
                    </div>
                    <KpiTile label={t('Vrijednost HP', 'HP value')} value={fmtE(hpVal)} />
                    <KpiTile label={t('Vrijednost GLS', 'GLS value')} value={fmtE(glsVal)} />
                </div>
                )
            ) : null}

            {/* Lookup po broju narudžbe */}
            {isHrms ? (
                <UiCard pad={16} style={{ marginTop: 14 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div onKeyDown={(e) => { if (e.key === 'Enter') void doLookup(); }}>
                            <TextInput value={lookupOrder} onChange={(v) => { setLookupOrder(v); if (!v.trim()) setLookupResult(undefined); }} icon="🔎" placeholder={t('Traži po broju narudžbe', 'Find by order number')} width={260} />
                        </div>
                        <Btn variant="primary" disabled={lookupBusy || !lookupOrder.trim()} onClick={() => void doLookup()}>{t('Traži', 'Find')}</Btn>
                        {lookupBusy ? <span style={{ fontSize: '0.8rem', color: ui.muted }}>{t('Tražim…', 'Searching…')}</span> : null}
                        {!lookupBusy && lookupResult === null ? (
                            <span style={{ fontSize: '0.8rem', fontStyle: 'italic', color: ui.muted }}>
                                {t('Nije pronađeno (nema povezane pošiljke za taj broj narudžbe)', 'Not found (no parcel linked to that order number)')}
                            </span>
                        ) : null}
                        {!lookupBusy && lookupResult ? (() => {
                            const lr = lookupResult;
                            const tone: 'ok' | 'info' | 'bad' | 'neutral' = lr.status_norm === 'delivered' ? 'ok' : lr.status_norm === 'in_transit' ? 'info' : lr.status_norm === 'returned' ? 'bad' : 'neutral';
                            const s = statusBadge(lr.status_norm, t);
                            return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                                    <Pill tone={tone}>{s.text}</Pill>
                                    <span style={{ fontSize: '0.82rem', color: ui.ink }}><strong>{t('Barkod', 'Barcode')}:</strong> {lr.barcode ?? '—'}</span>
                                    <span style={{ fontSize: '0.82rem', color: ui.ink }}><strong>{t('Kurir', 'Carrier')}:</strong> {lr.carrier ?? '—'}</span>
                                    {lr.sent_at ? <span style={{ fontSize: '0.8rem', color: ui.muted }}>{t('Poslano', 'Sent')}: {formatDateTime(lr.sent_at, locale)}</span> : null}
                                    {lr.delivery_hours != null ? <span style={{ fontSize: '0.8rem', color: ui.muted }}>{Math.round(Number(lr.delivery_hours))}h</span> : null}
                                    {lr.recipient_place ? <span style={{ fontSize: '0.8rem', color: ui.muted }}>{lr.recipient_place}</span> : null}
                                </div>
                            );
                        })() : null}
                    </div>
                </UiCard>
            ) : (
            <div style={{ ...card, marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                    type="text"
                    value={lookupOrder}
                    onChange={(e) => { setLookupOrder(e.target.value); if (!e.target.value.trim()) setLookupResult(undefined); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') void doLookup(); }}
                    placeholder={`🔎 ${t('Traži po broju narudžbe', 'Find by order number')}`}
                    style={{ ...inp, minWidth: 240 }}
                />
                <button type="button" onClick={() => void doLookup()} disabled={lookupBusy || !lookupOrder.trim()}
                    style={{ padding: '7px 12px', cursor: 'pointer', border: '1px solid rgba(var(--ad-soft-rgb),0.5)', borderRadius: 8, background: '#fffdf6', fontSize: '0.84rem', color: BROWN, fontWeight: 600 }}>
                    {t('Traži', 'Find')}
                </button>
                {lookupBusy ? <span className="muted" style={{ fontSize: '0.8rem' }}>{t('Tražim…', 'Searching…')}</span> : null}
                {!lookupBusy && lookupResult === null ? (
                    <span className="muted" style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                        {t('Nije pronađeno (nema povezane pošiljke za taj broj narudžbe)', 'Not found (no parcel linked to that order number)')}
                    </span>
                ) : null}
                {!lookupBusy && lookupResult ? (() => {
                    const lr = lookupResult;
                    const s = statusBadge(lr.status_norm, t);
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', background: '#faf6ec', border: '1px solid #efe6d2', borderRadius: 10, padding: '8px 14px' }}>
                            <span style={{ background: s.bg, color: s.fg, padding: '2px 10px', borderRadius: 999, fontSize: '0.74rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{s.text}</span>
                            <span style={{ fontSize: '0.82rem', color: '#3a2f17' }}><strong>{t('Barkod', 'Barcode')}:</strong> {lr.barcode ?? '—'}</span>
                            <span style={{ fontSize: '0.82rem', color: '#3a2f17' }}><strong>{t('Kurir', 'Carrier')}:</strong> {lr.carrier ?? '—'}</span>
                            {lr.sent_at ? <span className="muted" style={{ fontSize: '0.8rem' }}>{t('Poslano', 'Sent')}: {formatDateTime(lr.sent_at, locale)}</span> : null}
                            {lr.delivery_hours != null ? <span className="muted" style={{ fontSize: '0.8rem' }}>{Math.round(Number(lr.delivery_hours))}h</span> : null}
                            {lr.recipient_place ? <span className="muted" style={{ fontSize: '0.8rem' }}>{lr.recipient_place}</span> : null}
                        </div>
                    );
                })() : null}
            </div>
            )}

            {/* Tablica pregleda ili zaseban popis vraćenih */}
            {pregledMode === 'returned' ? (
                isHrms ? (
                    <UiCard pad={16} style={{ marginTop: 14 }}>
                        {returnedInner}
                    </UiCard>
                ) : (
                    <div style={{ ...card, marginTop: 14 }}>
                        {returnedInner}
                    </div>
                )
            ) : isHrms ? (
                <UiCard pad={0} style={{ marginTop: 14, overflow: 'hidden' }}>
                    {tableInner}
                </UiCard>
            ) : (
            <div style={{ ...card, marginTop: 14, padding: 0, overflow: 'hidden' }}>
                {tableInner}
            </div>
            )}
            </>) : null}
        </div>
    );
}

function KpiTile({ label, value, sub, bg, fg }: { label: string; value: string; sub?: string; bg?: string; fg?: string }) {
    return (
        <div style={{ ...card, padding: 14, background: bg ?? '#fff' }}>
            <div className="muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: fg ?? BROWN }}>{label}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: fg ?? '#3a2f17', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
            {sub ? <div style={{ fontSize: '0.78rem', fontWeight: 600, color: fg ?? BROWN, marginTop: 2 }}>{sub}</div> : null}
        </div>
    );
}

function SubTh({ children, muted }: { children: React.ReactNode; muted?: boolean }) {
    return <th style={{ ...th, color: muted ? '#aaa' : BROWN, fontWeight: 600 }}>{children}</th>;
}

function NumCell({ v, f, bold, muted }: { v: number; f: (n: number) => string; bold?: boolean; muted?: boolean }) {
    return <td style={{ ...tdR, fontWeight: bold ? 700 : 400, color: muted ? '#aaa' : '#3a2f17' }}>{f(v)}</td>;
}
